import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'

interface Cohort {
  cohort: string
  size: number
  retention: Record<number, number>
}

interface CohortResp {
  months: number
  cohorts: Cohort[]
  maxOffset: number
}

const PRESETS = [
  { months: 6, label: '6 luni' },
  { months: 12, label: '12 luni' },
  { months: 18, label: '18 luni' },
  { months: 24, label: '24 luni' },
]

type DisplayMode = 'pct' | 'count'

export function CohortShopifyPage() {
  const [months, setMonths] = useState(18)
  const [mode, setMode] = useState<DisplayMode>('pct')

  const query = useQuery<CohortResp>({
    queryKey: ['shopify-cohort', months],
    queryFn: async () =>
      (await api.get<CohortResp>(`/shopify/cohort-retention?months=${months}`)).data,
  })

  const data = query.data
  const offsets = useMemo(() => {
    if (!data) return []
    return Array.from({ length: data.maxOffset + 1 }, (_, i) => i)
  }, [data])

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Retenție pe cohorte Shopify</h1>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">Perioadă</label>
            <div className="mt-1 flex flex-wrap gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.months}
                  onClick={() => setMonths(p.months)}
                  className={`rounded border px-2 py-1 text-xs ${
                    months === p.months
                      ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                      : 'border-b4l-border bg-white hover:bg-b4l-bg'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">Afișare</label>
            <div className="mt-1 flex gap-1">
              <button
                onClick={() => setMode('pct')}
                className={`rounded border px-2 py-1 text-xs ${
                  mode === 'pct'
                    ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                    : 'border-b4l-border bg-white hover:bg-b4l-bg'
                }`}
              >
                Procent retention
              </button>
              <button
                onClick={() => setMode('count')}
                className={`rounded border px-2 py-1 text-xs ${
                  mode === 'count'
                    ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                    : 'border-b4l-border bg-white hover:bg-b4l-bg'
                }`}
              >
                Număr cumpărători
              </button>
            </div>
          </div>
        </div>
      </Card>

      <Card
        title="Matrice retention"
        description="Pe linii: luna primei comenzi (cohorta). Pe coloane: după N luni de la prima comandă, câți clienți au mai cumpărat."
      >
        {query.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {data && data.cohorts.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio cohortă găsită în perioada selectată.</p>
        )}
        {data && data.cohorts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border border-b4l-border bg-b4l-bg-soft px-2 py-1.5 text-left font-semibold sticky left-0 z-10">
                    Cohortă
                  </th>
                  <th className="border border-b4l-border bg-b4l-bg-soft px-2 py-1.5 text-right font-semibold">
                    Mărime
                  </th>
                  {offsets.map((o) => (
                    <th
                      key={o}
                      className="border border-b4l-border bg-b4l-bg-soft px-2 py-1.5 text-right font-semibold"
                    >
                      M{o}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c) => (
                  <tr key={c.cohort}>
                    <td className="border border-b4l-border bg-white px-2 py-1.5 font-mono font-medium sticky left-0 z-10">
                      {c.cohort}
                    </td>
                    <td className="border border-b4l-border bg-white px-2 py-1.5 text-right text-b4l-text-muted">
                      {c.size}
                    </td>
                    {offsets.map((o) => {
                      const count = c.retention[o] ?? 0
                      const pct = c.size > 0 ? (count / c.size) * 100 : 0
                      const display =
                        count === 0 ? '-' : mode === 'pct' ? `${pct.toFixed(0)}%` : count.toString()
                      return (
                        <td
                          key={o}
                          className="border border-b4l-border px-2 py-1.5 text-right"
                          style={{
                            background: heatColor(pct),
                            color: pct > 50 ? '#fff' : '#111',
                            fontWeight: o === 0 ? 600 : 400,
                          }}
                          title={`${c.cohort} → M${o}: ${count} clienți (${pct.toFixed(1)}%)`}
                        >
                          {display}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-xs text-b4l-text-muted">
          <p className="mb-1">
            <strong>Cum se citește:</strong> rândul {'"2025-01"'} arată ce s-a întâmplat cu clienții
            care au cumpărat prima dată în ianuarie 2025. Coloana <strong>M0</strong> = luna primei
            comenzi (mereu 100%). <strong>M1</strong> = câți s-au întors să cumpere în luna
            următoare. <strong>M6</strong> = câți s-au întors după 6 luni.
          </p>
          <p>
            <strong>Sănătos:</strong> retenția la M1 ≥ 15%, la M6 ≥ 8%, la M12 ≥ 5% pentru un
            magazin de suplimente. Sub aceste praguri = problemă de fidelizare.
          </p>
        </div>
      </Card>

      <Card title="Legendă culori (% retention)">
        <div className="flex flex-wrap gap-2">
          {[0, 5, 10, 20, 35, 50, 70, 100].map((p) => (
            <div
              key={p}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs"
              style={{ background: heatColor(p), color: p > 50 ? '#fff' : '#111' }}
            >
              {p}%
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

/** Returnează culoarea corespunzătoare procentului (0-100) — gradient de la alb la albastru închis. */
function heatColor(pct: number): string {
  if (pct === 0) return '#fff'
  // 0-20 = pal, 20-50 = albastru mediu, 50-100 = albastru închis
  const clamped = Math.min(100, Math.max(0, pct))
  const intensity = clamped / 100
  // Gradient: light blue (#e1ecf5) → blue (#378add) → dark blue (#0c447c)
  if (intensity <= 0.2) {
    const t = intensity / 0.2
    return blend('#f7fbff', '#cfe2f3', t)
  } else if (intensity <= 0.5) {
    const t = (intensity - 0.2) / 0.3
    return blend('#cfe2f3', '#7eb6dc', t)
  } else {
    const t = (intensity - 0.5) / 0.5
    return blend('#7eb6dc', '#0c447c', t)
  }
}

function blend(c1: string, c2: string, t: number): string {
  const a = hexToRgb(c1)
  const b = hexToRgb(c2)
  const r = Math.round(a.r + (b.r - a.r) * t)
  const g = Math.round(a.g + (b.g - a.g) * t)
  const bl = Math.round(a.b + (b.b - a.b) * t)
  return `rgb(${r}, ${g}, ${bl})`
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return { r: 255, g: 255, b: 255 }
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
}
